import {
	AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit, ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { Howl } from 'howler';
import * as _ from 'underscore';
import { UsersService } from 'src/app/users/users.service';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from 'src/app/shared/services/common.service';
import { BRM_PUBLIC_PAGE_CONFIG, buildPageStarts, groupsPerPageForWidth, textFitScale } from './charglink-brm-public.config';
import { BRM_PUBLIC_PAGE_ANIMATIONS } from './charglink-brm-public.animations';

const CONFIG = BRM_PUBLIC_PAGE_CONFIG;

/** One Ready to Pick box. `id` is the key the box is tracked by, so only boxes that change animate. */
interface ReadyBattery {
	id: string;
	name: string;
	/** True for the configured pulse time after the battery became Ready to Pick (never on first render). */
	isNew: boolean;
	/** Font scale that keeps a long battery ID inside its box. */
	fontScale: number;
}

/**
 * One group card on the public ranking page. A group can hold more than one Ready to Pick
 * battery — how many is the site's Ready to Pick Queue Count, capped at the configured
 * max per group (ST-2328, ST-2696).
 */
interface PublicRankingGroup {
	name: string;
	locations: ReadyBattery[];
	/** The group-level buzzer alert (`g[].af`), not the per-battery copy the API puts on the first Ready to Pick battery. */
	alertFlag: boolean;
	/** A group seen for the first time must not animate on its first paint. */
	isFirstSeen: boolean;
	/** Font scale that keeps a long group name inside the card header. */
	nameFontScale: number;
}

/** The groups currently on screen; keyed by `start` so a page change animates and a poll does not. */
interface PublicRankingPage {
	start: number;
	groups: PublicRankingGroup[];
}

const PAGE_IMAGES = {
	logo: '/sctportal/images/BRM/brm-logo-vertical.png',
	ready: '/sctportal/images/BRM/check.png',
	empty: '/sctportal/images/BRM/warning.png',
	batteryOn: '/sctportal/images/BRM/public_battery_on.png',
	batteryOff: '/sctportal/images/BRM/public_battery_off.png',
};
/** The cards' images, preloaded on init so a card never paints before its icon (e.g. warning.png on a first empty group). */
const CARD_ICONS = [PAGE_IMAGES.ready, PAGE_IMAGES.empty, PAGE_IMAGES.batteryOn, PAGE_IMAGES.batteryOff];

@Component({
	selector: 'app-charglink-brm-public',
	templateUrl: './charglink-brm-public.component.html',
	styleUrls: ['./charglink-brm-public.component.scss'],
	animations: BRM_PUBLIC_PAGE_ANIMATIONS,
})
export class CharglinkBrmPublicComponent implements OnInit, AfterViewInit, OnDestroy {
	readonly config = CONFIG;
	readonly images = PAGE_IMAGES;
	/** Kept referenced so the preloaded images stay in the browser's memory cache. */
	preloadedImages: HTMLImageElement[] = [];
	/** True once the card icons have loaded (or failed). */
	iconsReady = false;
	/** True once the first ranking data has arrived. */
	dataLoaded = false;
	groups: PublicRankingGroup[] = [];
	@Input() isTabMode = false;
	@Input() siteId = 0;
	customerId = 0;
	siteURL = '/';
	token = '';
	data = [];
	dataSorted = [];
	timeoutId;

	// buzzer sound
	buzzing: boolean = false;
	sound: Howl;
	buzzerEnabled: boolean = true;
	getChargLinkGroupsData: any;

	refreshTimerId;
	failedDataFetchCounter: number = 0;
	getRankEnables: boolean = true;
	isLoggedIn: boolean = false;
	isExpiredToken: boolean = false;
	siteSub: Subscription = new Subscription();

	// paging / rotation
	@ViewChild('groupsViewport') groupsViewport: ElementRef<HTMLElement>;
	@ViewChild('pageRoot') pageRoot: ElementRef<HTMLElement>;
	groupsPerPage = CONFIG.paging.breakpoints[0].groupsPerPage;
	pageStarts: number[] = [0];
	pageIndex = 0;
	visiblePages: PublicRankingPage[] = [];
	rotationTimerId;
	resizeObserver: ResizeObserver;
	/** Off until the first data has rendered, so nothing animates on first load. */
	animationsReady = false;
	/** Battery key → time its new-battery pulse ends. */
	newBatteryUntil: { [key: string]: number } = {};
	viewportWidth = 0;
	viewportHeight = 0;
	/** Tab mode has no fixed-height parent, so the page height is measured to fill the window below it. */
	tabModeHeight = 0;
	/** Every CSS custom property the stylesheet reads, bound once on the page root. Rebuilt when the viewport or the page changes. */
	cssVars = this.buildCssVars();

	constructor(
		private sideMenuService: SideMenuService,
		private route: ActivatedRoute,
		private sitesService: SitesService,
		private cdref: ChangeDetectorRef,
		private router: Router,
		private usersService: UsersService,
		private navigationService: NavigationService,
		private translateService: TranslateService,
		private commonService: CommonService,
		private ngZone: NgZone
	) {}

	ngOnInit() {
		if (!this.isTabMode) {
			this.token = this.route.snapshot.params['token'];
			this.siteId = this.route.snapshot.queryParams['siteId'] || 0;
		}
		const currentUser = this.usersService.getCurrentUser();
		this.siteSub = this.sideMenuService.currentSite.subscribe((siteInfo: any) => {
			if (!siteInfo || !siteInfo['id'])
				return;
			this.commonService.setPageTabTitle(`${siteInfo.customer_name} - ${siteInfo.name} ${this.translateService.instant('nav.ranking_page')}`);
		});
		if (currentUser?.id)
			this.isLoggedIn = true;

		this.preloadImages();
		this.loadBuzzerSetting();
		this.initSound();

		if (!this.isTabMode)
			this.sideMenuService.hide();

		this.getChargLinkGroups(false);

		if (!this.isTabMode)
			this.startPageRefreshTimer();
	}

	/** The loading indicator shows until both the first data and the card icons are in. */
	get isPageReady(): boolean {
		return this.dataLoaded && this.iconsReady;
	}

	preloadImages() {
		let pending = CARD_ICONS.length;
		const settle = () => {
			if (--pending <= 0)
				this.iconsReady = true;
		};

		this.preloadedImages = CARD_ICONS.map((src) => {
			const image = new Image();
			// A failed image must not hide the page — it settles the same as a loaded one.
			image.onload = settle;
			image.onerror = settle;
			image.src = src;
			return image;
		});
	}

	ngAfterViewInit() {
		// Measured after this check cycle — setting it during ngAfterViewInit would change a bound value mid-check.
		setTimeout(() => this.onWindowResize());
		this.observeViewport();
	}

	@HostListener('window:resize')
	onWindowResize() {
		this.updateTabModeHeight();

		// Browsers without ResizeObserver (older TV browsers) measure the group area on window resize instead.
		if (!this.resizeObserver && this.groupsViewport) {
			const viewport = this.groupsViewport.nativeElement;
			this.onViewportResize(viewport.clientWidth, viewport.clientHeight);
		}
	}

	updateTabModeHeight() {
		if (!this.isTabMode || !this.pageRoot)
			return;

		const top = this.pageRoot.nativeElement.getBoundingClientRect().top + window.scrollY;
		this.tabModeHeight = Math.max(0, window.innerHeight - top);
	}

	/** Groups per page follow the width left for the page (side nav included), not the window width. */
	observeViewport() {
		if (!this.groupsViewport || typeof ResizeObserver === 'undefined')
			return;

		this.resizeObserver = new ResizeObserver((entries) => {
			const { width, height } = entries[0].contentRect;
			this.ngZone.run(() => this.onViewportResize(width, height));
		});
		this.resizeObserver.observe(this.groupsViewport.nativeElement);
	}

	onViewportResize(width: number, height: number) {
		this.viewportWidth = width;
		this.viewportHeight = height;
		this.cssVars = this.buildCssVars();
		const groupsPerPage = groupsPerPageForWidth(width);
		if (groupsPerPage == this.groupsPerPage)
			return;

		this.groupsPerPage = groupsPerPage;
		this.updatePages();
	}

	/**
	 * Recalculates the pages after the groups or the groups per page changed. Keeps the current
	 * page when it still exists, otherwise goes back to the first one, so a page is never empty.
	 * The rotation timer is only started or stopped here — a poll never restarts it.
	 */
	updatePages() {
		this.pageStarts = buildPageStarts(this.groups.length, this.groupsPerPage);
		if (this.pageIndex >= this.pageStarts.length)
			this.pageIndex = 0;

		if (this.pageStarts.length > 1 && !this.rotationTimerId)
			this.rotationTimerId = setInterval(() => this.showNextPage(), CONFIG.paging.pageTimeMs);
		else if (this.pageStarts.length <= 1 && this.rotationTimerId)
			this.stopRotation();

		this.updateVisiblePage();
	}

	showNextPage() {
		this.pageIndex = (this.pageIndex + 1) % this.pageStarts.length;
		this.updateVisiblePage();
	}

	updateVisiblePage() {
		const start = this.pageStarts[this.pageIndex] || 0;
		this.visiblePages = [{ start, groups: this.groups.slice(start, start + this.groupsPerPage) }];
		this.cssVars = this.buildCssVars();
	}

	stopRotation() {
		clearInterval(this.rotationTimerId);
		this.rotationTimerId = null;
		this.pageIndex = 0;
	}

	buildCssVars(): { [name: string]: string } {
		const { newBatteryPulse, alertPulse } = CONFIG.animation;
		return {
			'--max-batteries': `${CONFIG.batteries.maxPerGroup}`,
			'--new-pulse-duration': `${newBatteryPulse.durationMs}ms`,
			'--new-pulse-count': `${newBatteryPulse.count}`,
			'--alert-pulse-duration': `${alertPulse.durationMs}ms`,
			'--vp-width': `${this.viewportWidth}px`,
			'--vp-height': `${this.viewportHeight}px`,
			'--groups-per-page': `${this.groupsPerPage}`,
			'--page-columns': `${this.visiblePages[0]?.groups.length || 1}`,
		};
	}

	startPageRefreshTimer() {
		this.refreshTimerId = setInterval(() => {
			window.location.reload();
		}, CONFIG.data.pageReloadMs);
	}

	getChargLinkGroups(noUiBlock=true) {
		let checkForUpdate = true;

		const filters = {
			siteId: this.siteId,
			token: this.token,
			fromDate: '',
			toDate: '',
			isHistory: false,
			publicPageReq: true
		};

		this.sitesService.getBRMGroupsData(filters, noUiBlock).subscribe((data: any) => {
			if ('siteId' in data)
				return this.updateSiteId(data.siteId);

			if (this.failedDataFetchCounter) {
				this.failedDataFetchCounter = 0;
				this.setPageAsConnectivityStatus(true);
			}

			data = this.mapResponseData(data);
			this.onFirstDataLoaded();
			
			if (data.site && data.site.id && data.site.customerid)
				this.siteURL = `/${data.site.customerid}/${data.site.id}`;

			let groups = data.groups;

			// Remove empty groups
			groups = _.filter(groups, function(group){ return group['groupname'];})

			if (!groups.length)
				return this.repeatGetChargLinkGroups();

			// getGroupDataById only catches an added group; a removed one is only seen by comparing the group lists.
			if (checkForUpdate && !_.isEqual(_.pluck(this.data, 'id').sort(), _.pluck(data.groups, 'id').sort())) {
				this.data = data.groups;
				checkForUpdate = false;
			}

			for (const group of groups) {
				group['groupname'] = group['groupname'].toUpperCase();
				const {thisGroup, checkUpdate} = this.getGroupDataById(group.id, data, checkForUpdate);
				checkForUpdate = checkUpdate;

				// A group with no Ready to Pick battery sends `c: []`, so no battery diff below ever sees
				// its alert start or end — the group's own alert flag is a change in itself (AC-15).
				if (checkForUpdate && thisGroup['alertFlag'] !== group.alertFlag) {
					this.data = data.groups;
					checkForUpdate = false;
				}

				const statuses = group.innergroups;

				for (const status in statuses) {
					let thisGroupStatusChargersById = {};
					let oldGroupIds = [];
					let newGroupIds = [];

					const groupStatuses = group['innergroups'][status];
					const oldGroupStatuses = thisGroup?.['innergroups']?.[status] || [];

					if (checkForUpdate) {
						oldGroupIds = _.pluck(oldGroupStatuses, 'id');
						newGroupIds = _.pluck(groupStatuses, "id");

						checkForUpdate = this.updateDomIfFindChange(oldGroupIds, newGroupIds, data);
					}

					thisGroupStatusChargersById = _.indexBy(oldGroupStatuses, 'id');
					for (const charger of groupStatuses) {
						if (checkForUpdate)
							this.updateChargesData(charger, thisGroupStatusChargersById, data);

					}
				}

				if (this.buzzerEnabled)
					this.checkAndPlayBuzzer(group);

				this.dataSorted = _.sortBy(this.data, ['innergroups[\'ready\']']).reverse();
			}

			// The alert outline follows `af` on every poll, so the sound does too: it stops on the first poll with no alerting group.
			// Only while it is sounding — Howler queues every stop() on a sound that has not loaded yet.
			if (this.buzzing && !_.some(groups, group => group.alertFlag))
				this.stopBuzzer();

			this.prepareGroupsData();

			this.repeatGetChargLinkGroups();
		}, () => {
			this.failedDataFetchCounter++;
			if (this.failedDataFetchCounter == CONFIG.data.offlineFailureThreshold)
				this.setPageAsConnectivityStatus(false);

			this.repeatGetChargLinkGroups();
		});
	}

	/** The buzzer sound (the largest file on the page) loads after the first data, so it never delays the page on a slow TV. */
	onFirstDataLoaded() {
		if (this.dataLoaded)
			return;

		this.dataLoaded = true;
		if (this.sound?.state?.() === 'unloaded')
			this.sound.load();
	}

	updateSiteId(siteId: number) {
		if (this.isTabMode)
			return;

		if (!siteId)
			return this.isExpiredToken = true;

		this.router.navigate(
			['charglink-brm', 'public', this.token],
			{ queryParams: {'siteId': siteId}, replaceUrl: true }
		);

		this.siteId = siteId;
		this.loadBuzzerSetting();

		this.getChargLinkGroups();
		return;
	}

	repeatGetChargLinkGroups() {
		this.getChargLinkGroupsData = setTimeout(() => {
			if(this.getRankEnables)
				this.getChargLinkGroups();
		}, CONFIG.data.pollIntervalMs);
	}

	mapResponseData(data: any) {
		const mappedData: any = {};
		mappedData.site = {
			customerid: data.s.cid,
			id: data.s.id,
		};

		mappedData.groups = [];

		data.g.forEach((group) => {
			const chargers = [];
			group.c.forEach((charger) => {
				chargers.push({
					id: charger.id,
					alertFlag: charger.af,
					currentSOC: charger.csoc,
					connected: charger.c,
					name: charger.n
				});
			});

			mappedData.groups.push(
				{
					groupname: group.gn,
					id: group.id,
					innergroups: {
						ready: chargers
					},
					alertFlag: group.af,
				}
			);

		});

		return mappedData;
	}

	getGroupDataById(id: number, data: any, checkForUpdate: boolean) {
		let thisGroup = {};

		for (const dataGroup of this.data) {
			if (dataGroup.id == id) {
				thisGroup = dataGroup;
				break;
			}
		}

		if (!thisGroup['id']) {
			this.data = data['groups'];
			checkForUpdate = false;
		}

		return {thisGroup, checkUpdate: checkForUpdate};
	}

	updateDomIfFindChange(oldIds, newIds, data) {
		let checkForUpdate = true;

		// Both directions matter: a battery being added to the ready list is as much a change as
		// one being removed, and a group can now hold two of them (ST-2328).
		const areEquals = _.isEmpty(_.difference(oldIds, newIds)) && _.isEmpty(_.difference(newIds, oldIds));
		if (!areEquals) {
			this.data = data['groups'];
			checkForUpdate = false;
			this.cdref.detectChanges();
		}

		return checkForUpdate;
	}

	updateChargesData(charger, thisGroupStatusChargersById, data) {
		for (const key in charger) {
			// update data if there is change in chargers list except for these attributes: ['relaxedBatteryTimer', 'time'];
			if (!['relaxedBatteryTimer', 'time'].includes(key)) {
				if (!thisGroupStatusChargersById[charger.id] || thisGroupStatusChargersById[charger.id][key] != charger[key]) {
					this.data = data.groups;
					break;
				}
			}
		}
	}

	checkAndPlayBuzzer(charger) {
		if (!charger.alertFlag)
			return;

		// Already sounding: extend it rather than letting it stop and restart on a later poll, which left a gap.
		if (this.buzzing) {
			this.scheduleBuzzerStop();
			return;
		}

		this.playBuzzer();
	}

	toggleBuzzer() {
		this.buzzerEnabled = !this.buzzerEnabled;
		this.saveBuzzerSetting();

		if (this.buzzerEnabled)
			this.sitesService.enableBuzzer();
		else
			this.stopBuzzer();
	}

	/** Per site, so muting one TV does not mute another site's page. Site 0 means the token has not resolved yet. */
	get buzzerStorageKey(): string {
		return +this.siteId ? `${CONFIG.buzzer.muteStorageKeyPrefix}${this.siteId}` : '';
	}

	/** Restores the Enable/Disable Buzzer choice so it survives the auto-reload. Defaults to enabled. */
	loadBuzzerSetting() {
		if (!this.buzzerStorageKey)
			return;

		try {
			const stored = localStorage.getItem(this.buzzerStorageKey);
			if (stored !== null)
				this.buzzerEnabled = stored === 'true';
		} catch (ex) {
			// Storage blocked (private mode, kiosk policy): keep the default, an enabled buzzer.
			this.buzzerEnabled = true;
		}
	}

	saveBuzzerSetting() {
		if (!this.buzzerStorageKey)
			return;

		try {
			localStorage.setItem(this.buzzerStorageKey, `${this.buzzerEnabled}`);
		} catch (ex) {
			// Storage blocked: the choice still applies until the page reloads.
		}
	}

	initSound() {
		const pc = this;

		this.sound = new Howl({
			src: [CONFIG.buzzer.src],
			format: ['mp3'],
			autoUnlock: true,
			loop: true,
			preload: false,

			// Howler calls this with `this` bound to the Howl, so the component is reached through `pc`.
			onplayerror: function() {
				pc.sound.once('unlock', function() {
					pc.playBuzzer();
				});
			}
		});
	}

	playBuzzer() {
		// A delayed audio-unlock retry must not play after the buzzer was disabled.
		if (!this.buzzerEnabled)
			return;

		// An alert can arrive before the deferred sound has loaded; Howler queues the play until it has.
		if (this.sound.state?.() === 'unloaded')
			this.sound.load();
		this.sound.play();
		this.buzzing = true;

		this.scheduleBuzzerStop();
	}

	/** Restarted by every poll that still has an alert, so it only runs out when the polls stop arriving. */
	scheduleBuzzerStop() {
		clearTimeout(this.timeoutId);
		this.timeoutId = setTimeout(() => this.stopBuzzer(), CONFIG.buzzer.playTimeMs);
	}

	stopBuzzer() {
		clearTimeout(this.timeoutId);
		this.timeoutId = null;
		this.buzzing = false;
		this.sound?.stop();
	}

	prepareGroupsData() {
		const groups: PublicRankingGroup[] = [];
		const groupObjects = _.indexBy(this.groups, 'name');
		const now = Date.now();
		const { count, durationMs } = CONFIG.animation.newBatteryPulse;
		const newBatteryUntil: { [key: string]: number } = {};

		for (const data of this.dataSorted) {
			if (!data.groupname)
				continue;

			// Every Ready to Pick battery in the group gets its own box, up to the configured max,
			// in API order (ST-2328, ST-2696). A battery with no ID is never dropped — the engine
			// says it is ready, so it must be shown; a missing ID is a data problem to surface,
			// not a reason to hide a battery.
			const chargers = (data.innergroups?.ready || []).slice(0, CONFIG.batteries.maxPerGroup);
			const previousGroup: PublicRankingGroup = groupObjects[data.groupname];
			const previousIds = previousGroup ? _.pluck(previousGroup.locations, 'id') : [];

			const locations: ReadyBattery[] = chargers.map((charger, index) => {
				const id = `${charger.id || `${charger.name || ''}#${index}`}`;
				const key = `${data.groupname}|${id}`;
				// A group seen for the first time does not pulse; later, an ID that was not in the
				// previous render pulses for the configured count × duration.
				if (previousGroup && !previousIds.includes(id))
					newBatteryUntil[key] = now + count * durationMs;
				else if (this.newBatteryUntil[key] > now)
					newBatteryUntil[key] = this.newBatteryUntil[key];

				const name = charger.name || '';
				const fontScale = textFitScale(name, CONFIG.textFit.locationFullSizeMaxChars);
				return { id, name, isNew: newBatteryUntil[key] > now, fontScale };
			});

			groups.push({
				name: data.groupname,
				locations,
				alertFlag: !!data.alertFlag,
				isFirstSeen: !previousGroup,
				nameFontScale: textFitScale(data.groupname, CONFIG.textFit.groupNameFullSizeMaxChars),
			});
		}

		this.newBatteryUntil = newBatteryUntil;
		this.groups = groups;
		this.updatePages();

		if (groups.length && !this.animationsReady)
			setTimeout(() => this.animationsReady = true);
	}

	setPageAsConnectivityStatus(isOnline: boolean) {
		if (!isOnline) {
			clearInterval(this.refreshTimerId)
			return;
		}

		if (!this.refreshTimerId && !this.isTabMode)
			this.startPageRefreshTimer();
	}

	ngOnDestroy() {
		if (!this.isTabMode)
			this.sideMenuService.show();
		
		if (this.sound)
			this.sound.stop();

		if(this.timeoutId)
			clearTimeout(this.timeoutId);

		if(this.getChargLinkGroupsData)
			clearTimeout(this.getChargLinkGroupsData);

		this.getRankEnables = false;
		if (this.refreshTimerId)
			clearInterval(this.refreshTimerId);

		if (this.rotationTimerId)
			clearInterval(this.rotationTimerId);

		if (this.resizeObserver)
			this.resizeObserver.disconnect();

		if (this.sound)
			this.sound.off('unlock');
	}

	// ngFor calls trackBy as (index, item); the item is the second argument.
	trackByGroup(_index: number, group: PublicRankingGroup): string {
		return group.name;
	}

	trackByPage(_index: number, page: PublicRankingPage): number {
		return page.start;
	}

	trackByBattery(_index: number, battery: ReadyBattery): string {
		return battery.id;
	}
}
